/**
 * lavaLampPipeline — lava lamp with morphing metaballs.
 *
 * Multiple spherical "blobs" move around via Lissajous orbits. The
 * smooth-min (smin) function blends them into a continuous fluid
 * surface. The blobs emit warm light, with the color shifting
 * between deep red, orange, and yellow based on screen position.
 *
 * Audio drives the blob orbit speeds and the lamp's overall glow.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface LavaLampPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

// Smooth-min for blending metaballs.
float smin(float a, float b, float k) {
  float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0);
  return mix(b, a, h) - k * h * (1.0 - h);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected screen coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.y *= 1.2;  // slight stretch so the blobs are taller than wide

  // Background: dark lamp glass with subtle vertical gradient.
  vec3 col = mix(vec3(0.04, 0.02, 0.06), vec3(0.10, 0.06, 0.16), vUV.y);

  // Subtle glass highlight.
  float glassHighlight = exp(-length(p - vec2(-0.3, 0.0)) * 4.0) * 0.05;
  col += vec3(1.0, 0.9, 0.7) * glassHighlight;

  // 5 metaballs with Lissajous orbits.
  const int N_BLOBS = 5;
  float d = 100.0;
  for (int i = 0; i < N_BLOBS; i++) {
    float fi = float(i);
    // Orbital parameters per blob.
    float ax = 0.15 + 0.10 * hash21(vec2(fi, 1.0));
    float ay = 0.20 + 0.15 * hash21(vec2(fi, 2.0));
    float fx = 0.4 + 0.3 * hash21(vec2(fi, 3.0));
    float fy = 0.5 + 0.3 * hash21(vec2(fi, 4.0));
    float px = hash21(vec2(fi, 5.0)) * 6.2831853;
    float py = hash21(vec2(fi, 6.0)) * 6.2831853;
    // Slow vertical drift.
    float drift = sin(uTime * 0.3 + fi * 1.7) * 0.3;
    vec2 blobPos = vec2(
      ax * sin(fx * uTime + px),
      ay * cos(fy * uTime + py) + drift
    );
    // Audio: blob radius pulses with bass.
    float radius = 0.12 + 0.04 * sin(uTime * 2.0 + fi) + bass * 0.04;
    // Distance from pixel to blob center.
    float di = length(p - blobPos) - radius;
    // Smooth blend.
    d = smin(d, di, 0.10);
  }

  // Color: deep red at the bottom, hot orange/yellow at the center.
  vec3 darkColor = vec3(0.30, 0.05, 0.02);
  vec3 midColor = vec3(0.95, 0.30, 0.05);
  vec3 hotColor = vec3(1.0, 0.85, 0.40);
  vec3 blobColor = mix(darkColor, midColor, smoothstep(0.0, 0.05, d));
  blobColor = mix(blobColor, hotColor, smoothstep(0.05, -0.05, d));
  // Glow: strongest near the surface.
  float surface = smoothstep(0.03, -0.03, d);
  col = mix(col, blobColor, surface);
  // Bloom: extend the glow around the blob.
  float bloom = exp(-max(d, 0.0) * 15.0);
  col += blobColor * bloom * (0.4 + high * 0.5);

  // Bottom highlight: lava lamp's bottom heating element.
  float bottomGlow = exp(-length(p - vec2(0.0, -0.6)) * 4.0);
  col += vec3(1.0, 0.7, 0.3) * bottomGlow * 0.4;

  // Top highlight: lamp's heat escape.
  float topGlow = exp(-length(p - vec2(0.0, 0.6)) * 5.0);
  col += vec3(0.95, 0.5, 0.2) * topGlow * 0.3;

  // Vignette.
  vec2 vp = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float vig = 1.0 - dot(vp, vp) * 0.3;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeLavaLampBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeLavaLampRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createLavaLampPipeline(gl: WebGL2RenderingContext): LavaLampPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawLavaLamp(
  gl: WebGL2RenderingContext,
  pipe: LavaLampPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
